import { useEffect, useRef, useState } from 'react';
import { useLogActivity } from '../activities/api.js';
import { useCreateTask } from '../tasks/api.js';
import { useLeadQuery, useLeadsQuery, useUpdateLead } from '../leads/api.js';
import { avatarColor, initialsOf } from './MobileNav.js';

type ActivityType = 'call' | 'meeting' | 'note' | 'task';
const OUTCOME_LABEL = { connected: 'Connected', voicemail: 'Left voicemail', no_answer: 'No answer' } as const;
type Outcome = keyof typeof OUTCOME_LABEL;

function formatFollowUp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(d) - startOfDay(now)) / 86_400_000);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (dayDiff === 0) return `Today ${time}`;
  if (dayDiff === 1) return `Tomorrow ${time}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`;
}

function defaultFollowUp(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return d.toISOString();
}

function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Mobile "Quick log" bottom sheet (design/orion-crm-design/rendered-html/
 * MobileQuickAdd.html) — mounted from the dashboard's floating "+" and lead
 * detail's "+ Log activity" bar, both of which pass a `leadId`. Call/
 * Meeting/Note route through the same `POST /activities/log` the desktop
 * composer uses; "Task" (the design's 4th segmented option) isn't a
 * loggable activity kind (`loggableActivityKindSchema` is note/call/email/
 * meeting), so it creates a real Task instead. */
export function QuickLogSheet({
  leadId: initialLeadId,
  onClose,
  onSaved,
  previewNotes,
}: {
  leadId?: string;
  onClose: () => void;
  onSaved?: () => void;
  previewNotes?: string;
}) {
  const [activityType, setActivityType] = useState<ActivityType>('call');
  const [outcome, setOutcome] = useState<Outcome>('connected');
  const [leadId, setLeadId] = useState(initialLeadId);
  const [pickingLead, setPickingLead] = useState(!initialLeadId);
  const [leadSearch, setLeadSearch] = useState('');
  const [notes, setNotes] = useState(previewNotes ?? '');
  const [followUpAt, setFollowUpAt] = useState<string | null>(defaultFollowUp());
  const followUpInputRef = useRef<HTMLInputElement>(null);

  // `initialLeadId` can resolve after mount (e.g. the dashboard's "+" opens
  // this before its own lead list has loaded) — react to it instead of only
  // reading it once, or the sheet gets stuck showing the lead picker even
  // after a real id arrives.
  useEffect(() => {
    if (initialLeadId) {
      setLeadId(initialLeadId);
      setPickingLead(false);
    }
  }, [initialLeadId]);

  const { data: lead } = useLeadQuery(leadId);
  const { data: searchResults } = useLeadsQuery({ q: pickingLead && leadSearch ? leadSearch : undefined, pageSize: 8 });
  const logActivity = useLogActivity();
  const createTask = useCreateTask();
  const updateLead = useUpdateLead(leadId ?? '');
  const saving = logActivity.isPending || createTask.isPending;

  const save = async () => {
    if (!notes.trim() && activityType !== 'task') return;
    const leadName = lead ? lead.fullName : undefined;
    if (activityType === 'task') {
      await createTask.mutateAsync({
        title: leadName ? `Follow up with ${leadName}` : 'Follow-up',
        description: notes.trim() || undefined,
        kind: 'todo',
        priority: 'normal',
        leadId,
        dueAt: followUpAt ?? undefined,
      });
    } else {
      await logActivity.mutateAsync({
        kind: activityType,
        bodyText: notes.trim() || '(no notes)',
        outcome: activityType === 'call' ? OUTCOME_LABEL[outcome] : undefined,
        leadId,
      });
      if (followUpAt && lead) {
        await updateLead.mutateAsync({ version: lead.version, nextFollowUpAt: followUpAt });
      }
    }
    onSaved?.();
    onClose();
  };

  return (
    <>
      <div aria-hidden="true" className="fixed inset-0 z-40 bg-[#12151b]/42" onClick={onClose} />
      <section role="dialog" aria-modal="true" aria-labelledby="ql-title" className="fixed inset-x-0 bottom-0 z-50 flex flex-col gap-4 rounded-t-[20px] bg-surface px-4 pb-6 pt-2 shadow-[0_-8px_32px_rgba(22,24,29,0.18)]">
        <span aria-hidden="true" className="mx-auto h-1 w-9 rounded-full bg-border-strong" />

        <div className="flex items-center">
          <h2 id="ql-title" className="m-0 flex-1 text-[18px] font-[650] tracking-[-0.01em]">
            Quick log
          </h2>
          <button type="button" aria-label="Close" onClick={onClose} className="flex size-11 items-center justify-center rounded-control">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <div role="radiogroup" aria-label="Activity type" className="grid h-11 grid-cols-4 gap-1 rounded-control bg-surface-subtle p-0.75">
          {(
            [
              { value: 'call' as const, label: 'Call', icon: <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /> },
              { value: 'meeting' as const, label: 'Meeting', icon: null },
              { value: 'note' as const, label: 'Note', icon: null },
              { value: 'task' as const, label: 'Task', icon: null },
            ]
          ).map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={activityType === opt.value}
              onClick={() => setActivityType(opt.value)}
              className={`flex items-center justify-center gap-1.5 rounded-chip text-sm ${activityType === opt.value ? 'bg-surface font-semibold text-ink shadow-card ring-1 ring-border' : 'font-medium text-text-secondary'}`}
            >
              {opt.icon && (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-primary">
                  {opt.icon}
                </svg>
              )}
              {opt.label}
            </button>
          ))}
        </div>

        {activityType === 'call' && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-[13px] font-semibold">Outcome</legend>
            <div className="flex gap-2">
              {(Object.keys(OUTCOME_LABEL) as Outcome[]).map((key) => {
                const selected = outcome === key;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setOutcome(key)}
                    className={`flex h-11 items-center gap-1.5 rounded-pill border px-3 text-sm ${selected ? 'border-primary bg-primary-soft font-semibold text-primary' : 'border-border-strong bg-surface font-medium text-text-secondary'}`}
                  >
                    {selected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m5 12 5 5 9-10" />
                      </svg>
                    )}
                    {OUTCOME_LABEL[key]}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}

        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-semibold">Related to</span>
          {lead && !pickingLead ? (
            <span className="flex h-11 w-fit items-center gap-2 rounded-pill border border-border bg-surface-subtle py-0.5 pl-1.5 pr-1">
              <span className="flex size-7.5 items-center justify-center rounded-full text-[11px] font-[650] text-white" style={{ background: avatarColor(lead.fullName) }}>
                {initialsOf(lead.fullName)}
              </span>
              <span className="text-sm font-semibold">
                {lead.fullName} {lead.companyName && <span className="font-normal text-text-muted">· {lead.companyName.split(' ')[0]}</span>}
              </span>
              <button type="button" aria-label="Change related record" onClick={() => setPickingLead(true)} className="flex size-9 items-center justify-center rounded-full text-text-secondary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </span>
          ) : (
            <div className="flex flex-col gap-1.5">
              <input
                autoFocus
                value={leadSearch}
                onChange={(e) => setLeadSearch(e.target.value)}
                placeholder="Search leads…"
                className="h-11 rounded-input border border-border-strong px-3 text-sm outline-none"
              />
              {leadSearch && (
                <ul className="m-0 max-h-40 overflow-y-auto rounded-control border border-border" style={{ listStyle: 'none' }}>
                  {(searchResults?.data ?? []).map((l) => (
                    <li key={l.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setLeadId(l.id);
                          setPickingLead(false);
                          setLeadSearch('');
                        }}
                        className="flex h-10 w-full items-center gap-2 border-b border-divider px-2.5 text-left text-sm last:border-b-0 hover:bg-surface-subtle"
                      >
                        <span className="font-semibold">{l.fullName}</span>
                        {l.companyName && <span className="text-text-muted">· {l.companyName}</span>}
                      </button>
                    </li>
                  ))}
                  {searchResults?.data.length === 0 && <li className="px-2.5 py-2 text-sm text-text-muted">No matches</li>}
                </ul>
              )}
            </div>
          )}
        </div>

        <label className="flex flex-col gap-2 text-[13px] font-semibold">
          Notes
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="h-22 resize-none rounded-input border border-border-strong bg-surface px-3 py-2.5 text-[15px] font-normal leading-5.5 text-ink outline-none focus:border-primary focus:shadow-focus-ring"
          />
        </label>

        <div className="relative">
          <button
            type="button"
            onClick={() => followUpInputRef.current?.showPicker?.()}
            className="flex h-13 w-full items-center gap-3 rounded-control border border-border px-2.5"
          >
            <span className="flex size-8 items-center justify-center rounded-chip bg-primary-soft">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-primary">
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M16 3v4M8 3v4M3 10h18" />
              </svg>
            </span>
            <span className="flex-1 text-left text-sm font-semibold">Set follow-up</span>
            <span className="text-sm font-semibold text-primary">{followUpAt ? formatFollowUp(followUpAt) : 'None'}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-text-secondary">
              <path d="m9 6 6 6-6 6" />
            </svg>
          </button>
          <input
            ref={followUpInputRef}
            type="datetime-local"
            aria-label="Follow-up date and time"
            value={followUpAt ? toDatetimeLocalValue(followUpAt) : ''}
            onChange={(e) => setFollowUpAt(e.target.value ? new Date(e.target.value).toISOString() : null)}
            className="pointer-events-none absolute inset-0 opacity-0"
          />
        </div>

        <button type="button" onClick={save} disabled={saving} className="flex h-12 w-full items-center justify-center rounded-control bg-primary text-base font-semibold text-white disabled:opacity-60">
          Save activity
        </button>
      </section>
    </>
  );
}
